/**
 * Mock messages data for development.
 * Provides realistic fitness-related conversations and messages.
 */
import { subDays, subHours, subMinutes } from 'date-fns';
import { MOCK_CURRENT_USER_ID, MOCK_USERS } from './mock-users.data';

const now = new Date();

/**
 * Helper to get user details for participant data.
 */
const getUserDetails = (userId) => {
  const user = MOCK_USERS.find((u) => u.id === userId);
  if (!user) return { displayName: 'Unknown', username: 'unknown', avatarUrl: '' };
  return {
    displayName: user.displayName,
    username: user.username,
    avatarUrl: user.avatarUrl,
  };
};

/**
 * Generate a deterministic conversation ID from two user IDs.
 */
const makeConversationId = (uid1, uid2) => {
  const sorted = [uid1, uid2].sort();
  return `${sorted[0]}_${sorted[1]}`;
};

// Conversation partners
const SARAH_ID = 'mock_user_002';
const MARCUS_ID = 'mock_user_003';
const PRIYA_ID = 'mock_user_004';
const JAKE_ID = 'mock_user_005';

// Conversation IDs
const CONV_SARAH = makeConversationId(MOCK_CURRENT_USER_ID, SARAH_ID);
const CONV_MARCUS = makeConversationId(MOCK_CURRENT_USER_ID, MARCUS_ID);
const CONV_PRIYA = makeConversationId(MOCK_CURRENT_USER_ID, PRIYA_ID);
const CONV_JAKE = makeConversationId(MOCK_CURRENT_USER_ID, JAKE_ID);

/**
 * Mock conversations.
 */
export const MOCK_CONVERSATIONS = [
  {
    id: CONV_SARAH,
    participants: [MOCK_CURRENT_USER_ID, SARAH_ID],
    participantDetails: {
      [MOCK_CURRENT_USER_ID]: getUserDetails(MOCK_CURRENT_USER_ID),
      [SARAH_ID]: getUserDetails(SARAH_ID),
    },
    lastMessage: {
      text: 'Sure! I usually do 5x5 with progressive overload. Let me send you the spreadsheet.',
      senderId: MOCK_CURRENT_USER_ID,
      timestamp: subMinutes(now, 12).toISOString(),
    },
    unreadCount: {
      [MOCK_CURRENT_USER_ID]: 0,
      [SARAH_ID]: 1,
    },
    typing: {
      [MOCK_CURRENT_USER_ID]: false,
      [SARAH_ID]: false,
    },
    createdAt: subDays(now, 14).toISOString(),
    updatedAt: subMinutes(now, 12).toISOString(),
  },
  {
    id: CONV_MARCUS,
    participants: [MOCK_CURRENT_USER_ID, MARCUS_ID],
    participantDetails: {
      [MOCK_CURRENT_USER_ID]: getUserDetails(MOCK_CURRENT_USER_ID),
      [MARCUS_ID]: getUserDetails(MARCUS_ID),
    },
    lastMessage: {
      text: 'That bench PR was insane! What program are you running?',
      senderId: MARCUS_ID,
      timestamp: subHours(now, 3).toISOString(),
    },
    unreadCount: {
      [MOCK_CURRENT_USER_ID]: 2,
      [MARCUS_ID]: 0,
    },
    typing: {
      [MOCK_CURRENT_USER_ID]: false,
      [MARCUS_ID]: false,
    },
    createdAt: subDays(now, 7).toISOString(),
    updatedAt: subHours(now, 3).toISOString(),
  },
  {
    id: CONV_PRIYA,
    participants: [MOCK_CURRENT_USER_ID, PRIYA_ID],
    participantDetails: {
      [MOCK_CURRENT_USER_ID]: getUserDetails(MOCK_CURRENT_USER_ID),
      [PRIYA_ID]: getUserDetails(PRIYA_ID),
    },
    lastMessage: {
      text: 'Can you share that meal prep recipe from your post? It looked amazing!',
      senderId: PRIYA_ID,
      timestamp: subDays(now, 1).toISOString(),
    },
    unreadCount: {
      [MOCK_CURRENT_USER_ID]: 1,
      [PRIYA_ID]: 0,
    },
    typing: {
      [MOCK_CURRENT_USER_ID]: false,
      [PRIYA_ID]: false,
    },
    createdAt: subDays(now, 5).toISOString(),
    updatedAt: subDays(now, 1).toISOString(),
  },
  {
    id: CONV_JAKE,
    participants: [MOCK_CURRENT_USER_ID, JAKE_ID],
    participantDetails: {
      [MOCK_CURRENT_USER_ID]: getUserDetails(MOCK_CURRENT_USER_ID),
      [JAKE_ID]: getUserDetails(JAKE_ID),
    },
    lastMessage: {
      text: 'See you at 6am tomorrow! Don\'t be late this time haha',
      senderId: JAKE_ID,
      timestamp: subDays(now, 2).toISOString(),
    },
    unreadCount: {
      [MOCK_CURRENT_USER_ID]: 0,
      [JAKE_ID]: 0,
    },
    typing: {
      [MOCK_CURRENT_USER_ID]: false,
      [JAKE_ID]: false,
    },
    createdAt: subDays(now, 21).toISOString(),
    updatedAt: subDays(now, 2).toISOString(),
  },
];

/**
 * Mock messages organized by conversation ID.
 */
export const MOCK_MESSAGES = {
  [CONV_SARAH]: [
    {
      id: 'msg_sarah_001',
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 14).toISOString(),
      type: 'system',
    },
    {
      id: 'msg_sarah_002',
      senderId: SARAH_ID,
      text: 'Hey Alex! I noticed we\'re accountability partners. Excited to work together!',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 14).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_003',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Hey Sarah! Me too! I\'ve been following your marathon training - really impressive consistency.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 14).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_004',
      senderId: SARAH_ID,
      text: 'Thank you! I saw your squat post - 315 for reps?! How long have you been powerlifting?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 13).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_005',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'About 3 years seriously. Started with Starting Strength then moved to 5/3/1.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 13).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_006',
      senderId: SARAH_ID,
      text: 'Nice! I\'ve been thinking about adding more strength work to complement my running. Any tips for a beginner?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 12).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_007',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Definitely! Start with compound movements - squats, deadlifts, bench. 2-3x per week is plenty alongside your running.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subDays(now, 12).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_008',
      senderId: SARAH_ID,
      text: 'That makes sense. How do you program your sets and reps?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, SARAH_ID],
      createdAt: subMinutes(now, 30).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_sarah_009',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Sure! I usually do 5x5 with progressive overload. Let me send you the spreadsheet.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID],
      createdAt: subMinutes(now, 12).toISOString(),
      type: 'text',
    },
  ],
  [CONV_MARCUS]: [
    {
      id: 'msg_marcus_001',
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 7).toISOString(),
      type: 'system',
    },
    {
      id: 'msg_marcus_002',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Hey Marcus! Love your training content. I\'ve been following your deadlift progression.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 7).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_003',
      senderId: MARCUS_ID,
      text: 'Thanks man! Always happy to connect with fellow lifters. What are your current numbers?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 7).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_004',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Bench 225, Squat 315, Deadlift 405. Trying to hit 1000lb total this year.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 6).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_005',
      senderId: MARCUS_ID,
      text: 'Solid numbers! You\'re close to that 1K club. What does your weekly split look like?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 6).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_006',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Upper/Lower 4 days a week. Monday upper, Tuesday lower, Thursday upper, Friday lower.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 5).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_007',
      senderId: MARCUS_ID,
      text: 'Classic split, works great. I run something similar. Want to be workout partners? We could push each other.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 4).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_008',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'That would be awesome! Are you at Iron Athletics gym?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 4).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_009',
      senderId: MARCUS_ID,
      text: 'Yeah! I\'m usually there around 6am. Early morning crew.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, MARCUS_ID],
      createdAt: subDays(now, 3).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_marcus_010',
      senderId: MARCUS_ID,
      text: 'That bench PR was insane! What program are you running?',
      imageUrl: null,
      readBy: [MARCUS_ID],
      createdAt: subHours(now, 3).toISOString(),
      type: 'text',
    },
  ],
  [CONV_PRIYA]: [
    {
      id: 'msg_priya_001',
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 5).toISOString(),
      type: 'system',
    },
    {
      id: 'msg_priya_002',
      senderId: PRIYA_ID,
      text: 'Hi Alex! I saw your meal prep Sunday post - those containers looked so organized!',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 5).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_priya_003',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Thanks Priya! It took a while to get my system down. I prep everything on Sunday for the whole week.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 5).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_priya_004',
      senderId: PRIYA_ID,
      text: 'How do you keep the chicken from getting dry by Thursday? That\'s always my problem.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 4).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_priya_005',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Great question! I brine it first, then cook to exactly 165F. Also I add a little broth to each container before sealing.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 4).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_priya_006',
      senderId: PRIYA_ID,
      text: 'The broth trick is genius! I\'m going to try that this weekend.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, PRIYA_ID],
      createdAt: subDays(now, 3).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_priya_007',
      senderId: PRIYA_ID,
      text: 'Can you share that meal prep recipe from your post? It looked amazing!',
      imageUrl: null,
      readBy: [PRIYA_ID],
      createdAt: subDays(now, 1).toISOString(),
      type: 'text',
    },
  ],
  [CONV_JAKE]: [
    {
      id: 'msg_jake_001',
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 21).toISOString(),
      type: 'system',
    },
    {
      id: 'msg_jake_002',
      senderId: JAKE_ID,
      text: 'Yo Alex! Saw we go to the same gym. Want to split a session sometime?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 21).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_003',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'For sure! I\'m usually there early mornings. What time do you go?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 20).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_004',
      senderId: JAKE_ID,
      text: '6am crew! Perfect. I\'m doing chest tomorrow if you want to join.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 20).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_005',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'I\'m in! I need a spotter for heavy bench anyway.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 19).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_006',
      senderId: JAKE_ID,
      text: 'Perfect. Brought the heat today - hit 315 bench for a triple! New PR.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 10).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_007',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Beast mode! That\'s huge. I\'m chasing 245 right now.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 10).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_008',
      senderId: JAKE_ID,
      text: 'You\'ll get there. Your form is solid, just keep grinding.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 9).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_009',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'Thanks man! Leg day tomorrow - you in?',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 3).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_010',
      senderId: JAKE_ID,
      text: 'Always. Heavy squats and RDLs.',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 3).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_011',
      senderId: MOCK_CURRENT_USER_ID,
      text: 'See you there!',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 2).toISOString(),
      type: 'text',
    },
    {
      id: 'msg_jake_012',
      senderId: JAKE_ID,
      text: 'See you at 6am tomorrow! Don\'t be late this time haha',
      imageUrl: null,
      readBy: [MOCK_CURRENT_USER_ID, JAKE_ID],
      createdAt: subDays(now, 2).toISOString(),
      type: 'text',
    },
  ],
};

/**
 * Get all mock conversations for a given user.
 */
export const getMockConversationsForUser = (userId) => {
  return MOCK_CONVERSATIONS
    .filter((c) => c.participants.includes(userId))
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
};

/**
 * Get mock messages for a conversation.
 */
export const getMockMessagesForConversation = (conversationId) => {
  return (MOCK_MESSAGES[conversationId] || []).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
};

/**
 * Get total unread count across all conversations for a user.
 */
export const getMockTotalUnread = (userId) => {
  return MOCK_CONVERSATIONS.reduce((total, conv) => {
    if (conv.participants.includes(userId)) {
      return total + (conv.unreadCount[userId] || 0);
    }
    return total;
  }, 0);
};

export { makeConversationId };
